import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import twilioClient from '../../utils/twilioClient';

const { FiSend, FiRefreshCw, FiFilter, FiChevronDown, FiChevronUp } = FiIcons;

const TwilioSmsConsole = () => {
  const [numbers, setNumbers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  const [formData, setFormData] = useState({
    to: '',
    from: '',
    body: ''
  });

  const [filters, setFilters] = useState({
    search: '',
    status: '',
    direction: ''
  });

  const quickTemplates = [
    'Test message from Twilio Control Panel',
    'Your appointment is confirmed for tomorrow at 2 PM',
    'Thank you for your inquiry. We will get back to you soon.',
    'Reminder: Your subscription expires in 3 days'
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [numbersData, messagesData] = await Promise.all([
        twilioClient.getNumbers(),
        twilioClient.getMessages()
      ]);
      setNumbers(numbersData.numbers || []);
      setMessages(messagesData.messages || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!formData.to || !formData.body) return;

    setSending(true);
    try {
      await twilioClient.sendMessage(formData);
      setFormData({ to: '', from: '', body: '' });
      loadData(); // Refresh messages
      // Show success toast (you can implement a toast system)
      alert('Message sent successfully!');
    } catch (error) {
      alert(`Failed to send message: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  const insertTemplate = (template) => {
    setFormData({ ...formData, body: template });
  };

  const toggleRowExpansion = (sid) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(sid)) {
      newExpanded.delete(sid);
    } else {
      newExpanded.add(sid);
    }
    setExpandedRows(newExpanded);
  };

  const filteredMessages = messages.filter(message => {
    if (filters.search && 
        !message.from.includes(filters.search) && 
        !message.to.includes(filters.search) &&
        !message.body.toLowerCase().includes(filters.search.toLowerCase())) {
      return false;
    }
    if (filters.status && message.status !== filters.status) return false;
    if (filters.direction && message.direction !== filters.direction) return false;
    return true;
  });

  return (
    <div className="space-y-8">
      {/* Send SMS Form */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h2 className="text-xl font-semibold mb-6">Send SMS</h2>
        
        <form onSubmit={handleSendMessage} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                To (E.164 format, e.g., +61412345678)
              </label>
              <input
                type="tel"
                value={formData.to}
                onChange={(e) => setFormData({ ...formData, to: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="+61412345678"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From
              </label>
              <select
                value={formData.from}
                onChange={(e) => setFormData({ ...formData, from: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Default Number</option>
                {numbers.map((number) => (
                  <option key={number.sid} value={number.phoneNumber}>
                    {number.phoneNumber} ({number.friendlyName})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message Body
            </label>
            <textarea
              value={formData.body}
              onChange={(e) => setFormData({ ...formData, body: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter your message..."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quick Templates
            </label>
            <div className="flex flex-wrap gap-2">
              {quickTemplates.map((template, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => insertTemplate(template)}
                  className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  {template.substring(0, 30)}...
                </button>
              ))}
            </div>
          </div>

          <button
            type="submit"
            disabled={sending || !formData.to || !formData.body}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <SafeIcon icon={sending ? FiRefreshCw : FiSend} className={`mr-2 ${sending ? 'animate-spin' : ''}`} />
            {sending ? 'Sending...' : 'Send SMS'}
          </button>
        </form>
      </div>

      {/* Message History */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Message History</h2>
            <button
              onClick={loadData}
              disabled={loading}
              className="flex items-center px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
            >
              <SafeIcon icon={FiRefreshCw} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input
              type="text"
              placeholder="Search by phone or message..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="queued">Queued</option>
              <option value="sent">Sent</option>
              <option value="delivered">Delivered</option>
              <option value="failed">Failed</option>
            </select>
            <select
              value={filters.direction}
              onChange={(e) => setFilters({ ...filters, direction: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Directions</option>
              <option value="inbound">Inbound</option>
              <option value="outbound-api">Outbound</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Direction
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  From
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  To
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Message
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredMessages.map((message) => (
                <React.Fragment key={message.sid}>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        message.direction === 'inbound' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {message.direction === 'inbound' ? 'In' : 'Out'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {message.from}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {message.to}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="max-w-xs truncate">
                        {message.body}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        message.status === 'delivered' ? 'bg-green-100 text-green-800' :
                        message.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                        message.status === 'failed' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {message.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(message.dateCreated).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => toggleRowExpansion(message.sid)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <SafeIcon icon={expandedRows.has(message.sid) ? FiChevronUp : FiChevronDown} />
                      </button>
                    </td>
                  </tr>
                  {expandedRows.has(message.sid) && (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 bg-gray-50">
                        <div className="space-y-2">
                          <div><strong>Full Message:</strong> {message.body}</div>
                          <div><strong>SID:</strong> {message.sid}</div>
                          {message.dateSent && (
                            <div><strong>Date Sent:</strong> {new Date(message.dateSent).toLocaleString()}</div>
                          )}
                          {message.errorMessage && (
                            <div className="text-red-600"><strong>Error:</strong> {message.errorMessage}</div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          
          {filteredMessages.length === 0 && !loading && (
            <div className="text-center py-8 text-gray-500">
              No messages found
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TwilioSmsConsole;